/* =====================================================================
 * useDragHandle — DragHandle 호버 추적 · 테이블 핸들 상태 · grip 클릭
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import type { Node } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';

/* ------------------------------------------------------------------ */
/* 훅 본체                                                              */
/* ------------------------------------------------------------------ */

interface UseDragHandleOptions {
  editor: Editor | null;
  /** EditorBody ref — 위치 계산용 */
  editorBodyRef: React.RefObject<HTMLDivElement | null>;
}

export function useDragHandle({ editor, editorBodyRef }: UseDragHandleOptions) {
  /* DragHandle onNodeChange에서 추적 (ref — 렌더 유발 없음) */
  const nodeInfoRef = useRef<{ node: Node | null; pos: number }>({ node: null, pos: 0 });
  const tableHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── 테이블 핸들 상태 ── */
  const [handleInsideTable, setHandleInsideTable] = useState(false);
  const [tableHandleStyle, setTableHandleStyle] = useState<React.CSSProperties>({});
  const hoveringCustomHandleRef = useRef(false);


  /* ── handleNodeChange — DragHandle 확장이 호버된 노드를 알려줌 ── */
  const handleNodeChange = useCallback(
    ({ node, pos }: { node: Node | null; pos: number }) => {
      if (!node || !editor) {
        // ★ 테이블 핸들 위에 있거나 테이블 모드일 때는 nodeInfoRef를 초기화하지 않음
        if (!hoveringCustomHandleRef.current && !handleInsideTable) {
          nodeInfoRef.current = { node, pos };
        }
        if (tableHideTimerRef.current) clearTimeout(tableHideTimerRef.current);
        tableHideTimerRef.current = setTimeout(() => {
          if (!hoveringCustomHandleRef.current) {
            setHandleInsideTable(false);
            nodeInfoRef.current = { node: null, pos: 0 };
          }
        }, 500);
        return;
      }

      // 컨테이너 블록 내부인지 체크 (테이블 / blockquote / details / taskList)
      let isInsideTable = false;
      let tableNode: Node | null = null;
      let tablePos = 0;
      // 테이블 이외 컨테이너 (blockquote, details, taskList)
      const CONTAINER_TYPES = new Set(['blockquote', 'details', 'taskList']);
      let containerNode: Node | null = null;
      let containerPos = 0;

      try {
        const inside = Math.min(pos + 1, editor.state.doc.content.size - 1);
        const $pos = editor.state.doc.resolve(inside);
        for (let d = $pos.depth; d >= 1; d--) {
          const n = $pos.node(d);
          if (n.type.name === 'table') {
            isInsideTable = true;
            tableNode = n;
            tablePos = $pos.before(d);
            break;
          }
          if (CONTAINER_TYPES.has(n.type.name) && $pos.depth > d) {
            containerNode = n;
            containerPos = $pos.before(d);
            // 계속 올라가서 table이 있는지 확인 (table > blockquote 같은 경우)
          }
        }
      } catch { /* 방어 */ }

      // 컨테이너 블록 내부 → 기본 핸들 숨기고 커스텀 핸들 표시 (테이블과 동일 패턴)
      if (!isInsideTable && containerNode) {
        if (tableHideTimerRef.current) {
          clearTimeout(tableHideTimerRef.current);
          tableHideTimerRef.current = null;
        }
        nodeInfoRef.current = { node: containerNode, pos: containerPos };
        setHandleInsideTable(true);

        // 컨테이너 DOM 위치 계산 → 커스텀 핸들 위치
        try {
          const dom = editor.view.nodeDOM(containerPos) as HTMLElement | null;
          if (dom && editorBodyRef.current) {
            const rect = dom.getBoundingClientRect();
            const bodyRect = editorBodyRef.current.getBoundingClientRect();
            const scrollTop = editorBodyRef.current.scrollTop;
            const scrollLeft = editorBodyRef.current.scrollLeft;

            setTableHandleStyle({
              position: 'absolute',
              top: rect.top - bodyRect.top + scrollTop + 10,
              left: rect.left - bodyRect.left + scrollLeft - 52,
              zIndex: 50,
            });
          }
        } catch { /* 방어 */ }
        return;
      }

      if (isInsideTable && tableNode) {
        // 타이머 취소 — 테이블 안에 있으므로 숨기면 안 됨
        if (tableHideTimerRef.current) {
          clearTimeout(tableHideTimerRef.current);
          tableHideTimerRef.current = null;
        }
        nodeInfoRef.current = { node: tableNode, pos: tablePos };
        setHandleInsideTable(true);

        // 테이블 DOM 위치 계산 → 커스텀 핸들 위치
        try {
          const dom = editor.view.nodeDOM(tablePos) as HTMLElement | null;
          const wrapper = dom?.closest('.tableWrapper') as HTMLElement | null;
          const target = wrapper || dom;
          if (target && editorBodyRef.current) {
            const rect = target.getBoundingClientRect();
            const bodyRect = editorBodyRef.current.getBoundingClientRect();
            const scrollTop = editorBodyRef.current.scrollTop;
            const scrollLeft = editorBodyRef.current.scrollLeft;
            setTableHandleStyle({
              position: 'absolute',
              top: rect.top - bodyRect.top + scrollTop + 16,
              left: rect.left - bodyRect.left + scrollLeft - 62,
              zIndex: 50,
            });
          }
        } catch { /* 방어 */ }
      } else {
        nodeInfoRef.current = { node, pos };
        // 비-테이블 블록 → 즉시 숨김 (커스텀 핸들 위에 있지 않을 때만)
        if (tableHideTimerRef.current) {
          clearTimeout(tableHideTimerRef.current);
          tableHideTimerRef.current = null;
        }
        if (!hoveringCustomHandleRef.current) {
          setHandleInsideTable(false);
        }
      }
    },
    [editor, handleInsideTable, editorBodyRef],
  );

  /* ── grip 클릭: 블록 선택 (주황색 하이라이트) ── */
  const handleGripClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!editor) return;
      const { node, pos } = nodeInfoRef.current;
      if (!node) return;
      const tr = editor.state.tr.setSelection(
        NodeSelection.create(editor.state.doc, pos),
      );
      editor.view.dispatch(tr);
    },
    [editor],
  );

  /* 테이블 핸들 타이머 정리 */
  useEffect(() => {
    return () => {
      if (tableHideTimerRef.current) clearTimeout(tableHideTimerRef.current);
    };
  }, []);

  /* nested 옵션 — 컴포넌트 밖에서 선언하면 re-render 방지 */
  const nestedConfig = useMemo(() => ({ edgeDetection: { threshold: -16 } }), []);

  return {
    nodeInfoRef,
    handleInsideTable,
    setHandleInsideTable,
    tableHandleStyle,
    hoveringCustomHandleRef,
    tableHideTimerRef,
    handleNodeChange,
    handleGripClick,
    nestedConfig,
  };
}
